import React, { useState, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PartGroup, YedekParcaData } from '../types/ServiceRequest';
import { searchYedekParca } from '../services/serviceRequestService';
import SparePartInfoModal from './SparePartInfoModal';
import YedekParcaModal from './SparePartModal';
import PartGroupSelector from './PartGroupSelector';
import ProductImage from './ProductImage';
import { useRipple } from '../hooks/useRipple';

// Seçilen yedek parça interface'i
interface SelectedSparePart {
  part: YedekParcaData;
  quantity: number;
}

interface PartSelectionStepProps {
  selectedSpareParts: SelectedSparePart[];
  loadingPartGroups: boolean;
  partGroups: PartGroup[];
  partGroupError: string;
  productData: { Model: number } | null;
  onBackToIssueDetails: () => void;
  onContinue: () => void;
  onPartUpdate: (updatedParts: SelectedSparePart[]) => void;
  onShowMessage?: (message: string, type?: 'info' | 'success' | 'warning' | 'error') => void;
}

const PartSelectionStep: React.FC<PartSelectionStepProps> = ({
  selectedSpareParts,
  loadingPartGroups,
  partGroups,
  partGroupError,
  productData,
  onBackToIssueDetails,
  onContinue,
  onPartUpdate,
  onShowMessage
}) => {
  // Modal state'leri - artık component içinde yönetiliyor
  const [sparePartInfoModalOpen, setSparePartInfoModalOpen] = useState(false);
  const [currentSparePartInfo, setCurrentSparePartInfo] = useState<{part: YedekParcaData; quantity: number; index?: number} | null>(null);
  
  // Parça grubu ve parça listesi modal state'leri
  const [isPartGroupSheetOpen, setIsPartGroupSheetOpen] = useState(false);
  const [yedekParcaModalOpen, setYedekParcaModalOpen] = useState(false);
  const [selectedPartGroup, setSelectedPartGroup] = useState<PartGroup | null>(null);
  const [yedekParcalar, setYedekParcalar] = useState<YedekParcaData[]>([]);
  const [yedekParcaSearchTerm, setYedekParcaSearchTerm] = useState('');
  const [loadingModalSearch, setLoadingModalSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingGroupId, setLoadingGroupId] = useState<number | null>(null);
  const [shouldFadeOnClose, setShouldFadeOnClose] = useState(false);
  
  // Performance optimization: sadece ilk N item'ı göster, çok fazla varsa
  const [showAllItems, setShowAllItems] = useState(false);
  const maxVisibleItems = 20;
  
  // Ripple hook
  const { ripples, createRipple } = useRipple<number>();
  

  
  // Parça düzenleme handler'ı
  const handleEditSparePart = (event: React.MouseEvent, index: number) => {
    // Önce ripple efektini başlat
    const partId = selectedSpareParts[index].part.Id;
    createRipple(event, partId, () => {
      const sparePart = selectedSpareParts[index];
      setCurrentSparePartInfo({
        part: sparePart.part,
        quantity: sparePart.quantity,
        index: index
      });
      setSparePartInfoModalOpen(true);
    });
  };

  // SparePartInfoModal handler'ları
  const handleSparePartQuantityChange = (quantity: number) => {
    if (currentSparePartInfo) {
      setCurrentSparePartInfo({
        ...currentSparePartInfo,
        quantity
      });
    }
  };

  const handleSparePartSave = () => {
    if (currentSparePartInfo) {
      const newSparePart: SelectedSparePart = {
        part: currentSparePartInfo.part,
        quantity: currentSparePartInfo.quantity
      };

      if (currentSparePartInfo.index !== undefined) {
        // Güncelleme
        const updatedParts = [...selectedSpareParts];
        updatedParts[currentSparePartInfo.index] = newSparePart;
        onPartUpdate(updatedParts);
      } else {
        // Yeni ekleme
        onPartUpdate([...selectedSpareParts, newSparePart]);
      }

      setCurrentSparePartInfo(null);
      setSparePartInfoModalOpen(false);
    }
  };

  // Parça kaldırma handler'ı
  const handleRemoveSparePart = useCallback((index: number) => {
    const updatedParts = selectedSpareParts.filter((_, i) => i !== index);
    onPartUpdate(updatedParts);
  }, [selectedSpareParts, onPartUpdate]);

  // Parça grup seçim handler'ı
  const handlePartGroupSelect = async (group: PartGroup | null) => {
    if (!group || !productData) return;

    setSelectedPartGroup(group);
    setSearchTerm('');
    setYedekParcaSearchTerm('');
    setLoadingModalSearch(true);
    setLoadingGroupId(group.Id); // Yüklenen grup ID'sini işaretle

    try {
      // Seçilen parça grubuna göre yedek parçaları getir
      const result = await searchYedekParca('', group, productData.Model);
      setYedekParcalar(result.data || []);
      
      // Modal geçişinde fade animasyonu kullanılacağını işaretle
      setShouldFadeOnClose(true);
      
      // Modal geçişi - hemen değiştir
      setYedekParcaModalOpen(true);
      setIsPartGroupSheetOpen(false);
    } catch (error) {
      console.error('Yedek parça grubu yüklenirken hata:', error);
      setYedekParcalar([]);
      
      // Hata durumunda da fade animasyonu kullan
      setShouldFadeOnClose(true);
      
      // Hata durumunda da aynı strateji - hemen değiştir
      setYedekParcaModalOpen(true);
      setIsPartGroupSheetOpen(false);
    } finally {
      setLoadingModalSearch(false);
      setLoadingGroupId(null); // Yükleme bittiğinde loader'ı kaldır
    }
  };

  // Yedek parça seçim handler'ı
  const handleSelectYedekParca = (selectedPart: YedekParcaData) => {
    // Seçilen parça listede zaten var mı kontrol et
    const existingPartIndex = selectedSpareParts.findIndex(
      sparePart => sparePart.part.Id === selectedPart.Id
    );

    if (existingPartIndex !== -1) {
      // Parça zaten listede var, miktarını artır
      const updatedParts = [...selectedSpareParts];
      const oldQuantity = updatedParts[existingPartIndex].quantity;
      const newQuantity = oldQuantity + 1;
      
      updatedParts[existingPartIndex] = {
        ...updatedParts[existingPartIndex],
        quantity: newQuantity
      };
      
      onPartUpdate(updatedParts);
      setYedekParcaModalOpen(false);
      
      // Kullanıcıya bilgi ver
      if (onShowMessage) {
        onShowMessage(
          `"${selectedPart.Ad}" parçasının miktarı ${oldQuantity}'den ${newQuantity}'e artırıldı.`,
          'info'
        );
      }
    } else {
      // Yeni parça, modal ile ekle
      setCurrentSparePartInfo({
        part: selectedPart,
        quantity: 1,
        index: undefined
      });
      
      // Modal geçişi - hemen değiştir
      setSparePartInfoModalOpen(true);
      setYedekParcaModalOpen(false);
    }
  };

  // Parça ekle butonu handler'ı
  const handleAddPartClick = () => {
    setShouldFadeOnClose(false); // Modal açılırken fade durumunu sıfırla
    setIsPartGroupSheetOpen(true);
  };
  return (
    <div className="max-w-md mx-auto">
      <div className="flex justify-center mb-8">
        <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-200 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-2xl flex items-center justify-center shadow-lg">
          <svg className="w-12 h-12 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </div>
      </div>

      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-medium text-gray-900 dark:text-white">Parça Seçimi</h2>
        <button
          onClick={onBackToIssueDetails}
          className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center"
        >
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Geri Dön
        </button>
      </div>

      {/* Seçilen Yedek Parçalar Listesi */}
      <div className="space-y-4 mb-6">
        {selectedSpareParts.length > 0 ? (
          <>
            <AnimatePresence>
              {(showAllItems ? selectedSpareParts : selectedSpareParts.slice(0, maxVisibleItems)).map((selectedPart, index) => {
              // Çok fazla item varsa animasyon delay'ini azalt
              const animationDelay = selectedSpareParts.length > 10 ? index * 0.02 : index * 0.1;
              
              return (
                <motion.div
                  key={selectedPart.part.Id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3, delay: animationDelay }}
                  className="border border-gray-200 dark:border-gray-600 rounded-lg bg-gradient-to-br from-gray-50 via-slate-50 to-gray-100 dark:from-gray-700 dark:via-gray-700/90 dark:to-gray-600 cursor-pointer hover:from-gray-100 hover:via-slate-100 hover:to-gray-200 dark:hover:from-gray-600 dark:hover:via-gray-600/90 dark:hover:to-gray-500 transition-all duration-200 relative overflow-hidden"
                  onClick={(event) => handleEditSparePart(event, index)}
                  whileTap={{ scale: 0.98 }}
                >
                {/* Ripple Effects */}
                {ripples[selectedPart.part.Id]?.map((ripple) => (
                  <motion.div
                    key={ripple.id}
                    className="absolute rounded-full bg-blue-400/30 pointer-events-none"
                    style={{
                      left: ripple.x - (ripple.size || 50) / 2,
                      top: ripple.y - (ripple.size || 50) / 2,
                      width: ripple.size || 50,
                      height: ripple.size || 50,
                    }}
                    initial={{ scale: 0.1, opacity: 0.6 }}
                    animate={{ scale: 1, opacity: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                  />
                ))}
                <div className="rounded-lg p-4 border border-white dark:border-gray-700">
                <div className="flex items-start gap-x-3">
                {/* Parça Görseli */}
                <div className="flex-shrink-0">
                  <ProductImage
                    imageId={selectedPart.part.Gorsel}
                    alt={selectedPart.part.Ad}
                    size="w-16 h-16"
                    iconSize="w-8 h-8"
                  />
                </div>

                {/* Parça Bilgileri */}
                <div className="flex-1 min-w-0">
                  <h4 className="text-base font-semibold text-gray-900 dark:text-white line-clamp-2">
                    {selectedPart.part.Ad}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {selectedPart.part.Kod}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {selectedPart.part.GrupAdi}
                  </p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Miktar: {selectedPart.quantity} adet
                  </p>
                </div>

                {/* İşlem Butonları - Sağda küçük simgeler */}
                <div className="flex-shrink-0 -ml-1.5">
                  <div className="flex flex-col space-y-1">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Düğmeye tıklandığında ripple efekti olmadan direkt düzenleme modalını aç
                        const sparePart = selectedSpareParts[index];
                        setCurrentSparePartInfo({
                          part: sparePart.part,
                          quantity: sparePart.quantity,
                          index: index
                        });
                        setSparePartInfoModalOpen(true);
                      }}
                      className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 border border-transparent hover:border-blue-200 dark:hover:border-blue-700 p-1 rounded-lg transition-all duration-200"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Silme işleminde ripple efekti yok, direkt sil
                        handleRemoveSparePart(index);
                      }}
                      className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 border border-transparent hover:border-red-200 dark:hover:border-red-700 p-1 rounded-lg transition-all duration-200"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
                </div>
              </div>
            </motion.div>
              );
            })}
          </AnimatePresence>
          
          {/* Daha fazla göster butonu */}
          {selectedSpareParts.length > maxVisibleItems && !showAllItems && (
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={() => setShowAllItems(true)}
              className="w-full mt-4 border border-gray-200 dark:border-gray-600 rounded-lg bg-gradient-to-br from-gray-100 via-slate-100 to-gray-200 dark:from-gray-700 dark:via-gray-700/90 dark:to-gray-600 hover:from-gray-200 hover:via-slate-200 hover:to-gray-300 dark:hover:from-gray-600 dark:hover:via-gray-600/90 dark:hover:to-gray-500 transition-all duration-200"
            >
              <div className="rounded-lg py-3 px-4 border border-white dark:border-gray-700 text-gray-700 dark:text-gray-300 font-medium">
                Daha fazla göster ({selectedSpareParts.length - maxVisibleItems} daha)
              </div>
            </motion.button>
          )}
          
          {/* Daha az göster butonu */}
          {showAllItems && selectedSpareParts.length > maxVisibleItems && (
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={() => setShowAllItems(false)}
              className="w-full mt-4 border border-gray-200 dark:border-gray-600 rounded-lg bg-gradient-to-br from-gray-100 via-slate-100 to-gray-200 dark:from-gray-700 dark:via-gray-700/90 dark:to-gray-600 hover:from-gray-200 hover:via-slate-200 hover:to-gray-300 dark:hover:from-gray-600 dark:hover:via-gray-600/90 dark:hover:to-gray-500 transition-all duration-200"
            >
              <div className="rounded-lg py-3 px-4 border border-white dark:border-gray-700 text-gray-700 dark:text-gray-300 font-medium">
                Daha az göster
              </div>
            </motion.button>
          )}
          </>
        ) : (
          <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg bg-gradient-to-br from-gray-50 via-slate-50 to-gray-100 dark:from-gray-700 dark:via-gray-700/90 dark:to-gray-600">
            <div className="rounded-lg py-8 border border-white dark:border-gray-700 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Henüz yedek parça seçilmedi</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Eklemek için aşağıdaki düğmeyi kullanın</p>
            </div>
          </div>
        )}
      </div>

      {/* Parça Ekle Butonu */}
      {loadingPartGroups ? (
        <div className="flex items-center justify-center py-8">
          <svg className="animate-spin h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <span className="ml-2 text-gray-600 dark:text-gray-400">Parça grupları yükleniyor...</span>
        </div>
      ) : partGroups.length === 0 ? (
        <div className="bg-yellow-50 dark:bg-yellow-900/50 border-l-4 border-yellow-500 p-4 rounded mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700 dark:text-yellow-200">
                {partGroupError || 'Bu kategori için parça grubu bulunamadı.'}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={handleAddPartClick}
          className="w-full flex items-center justify-center px-6 py-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-500 dark:hover:border-blue-400 transition-colors group"
        >
          <svg className="w-6 h-6 text-gray-400 group-hover:text-blue-500 dark:group-hover:text-blue-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          <span className="text-base font-medium text-gray-700 dark:text-gray-300 group-hover:text-blue-700 dark:group-hover:text-blue-300">
            Yedek Parça Ekle
          </span>
        </button>
      )}

      {/* Progress Indicator */}
      {useMemo(() => (
        <div className="flex items-center mt-6 mb-6">
          <div className="flex-1 bg-gray-200 dark:bg-gray-700 h-0.5 rounded-full"></div>
          <div className="px-4 text-sm text-gray-500 dark:text-gray-400">
            {selectedSpareParts.length === 0 ? 'Hiç parça seçilmedi' : `${selectedSpareParts.length} parça seçildi`}
          </div>
          <div className="flex-1 bg-gray-200 dark:bg-gray-700 h-0.5 rounded-full"></div>
        </div>
      ), [selectedSpareParts.length])}

      {/* Devam Et Butonu */}
      <button
        type="button"
        onClick={onContinue}
        disabled={loadingPartGroups || partGroups.length === 0}
        className={`
          w-full flex items-center justify-center px-6 py-3.5
          text-base font-semibold text-white
          bg-gradient-to-r from-blue-600 to-indigo-600
          hover:from-blue-700 hover:to-indigo-700
          focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
          rounded-lg shadow-lg shadow-blue-500/25
          transform transition-all duration-200
          hover:scale-[1.02] hover:shadow-xl hover:shadow-blue-500/30
          active:scale-[0.98]
          disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100
          disabled:hover:shadow-lg disabled:hover:shadow-blue-500/25
          relative overflow-hidden
          before:absolute before:inset-0 before:bg-gradient-to-r before:from-white/10 before:via-transparent before:to-white/10 before:translate-x-[-100%] hover:before:translate-x-[100%] before:transition-transform before:duration-700
        `}
      >
        Devam Et
      </button>

      {/* Parça Grubu Seçim Modal */}
      <PartGroupSelector
        isOpen={isPartGroupSheetOpen}
        onClose={() => {
          setIsPartGroupSheetOpen(false);
          setShouldFadeOnClose(false); // Fade durumunu temizle
        }}
        onSelect={handlePartGroupSelect}
        groups={partGroups}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        selectedGroupId={undefined}
        loadingGroupId={loadingGroupId}
        fadeOnClose={shouldFadeOnClose}
      />

      {/* Yedek Parça Listesi Modal */}
      <YedekParcaModal
        isOpen={yedekParcaModalOpen}
        onClose={() => {
          setYedekParcaModalOpen(false);
        }}
        onSelect={handleSelectYedekParca}
        onSearch={(term: string) => {
          setYedekParcaSearchTerm(term);

          if (!productData) {
            setYedekParcalar([]);
            return;
          }

          setLoadingModalSearch(true); // Modal içindeki arama için loading

          // Seçili parça grubunda arama yap
          const groupToSearch = selectedPartGroup || { Id: 0, Ad: '' };
          searchYedekParca(term, groupToSearch, productData.Model)
            .then(result => {
              // Modal hala açıksa, sonuçları güncelle
              if (yedekParcaModalOpen) {
                setYedekParcalar(result.data);
              }
            })
            .catch(error => {
              console.error('Modal içinde yedek parça arama hatası:', error);
              setYedekParcalar([]);
            })
            .finally(() => {
              setLoadingModalSearch(false); // Modal içindeki arama loading'i bitir
            });
        }}
        parts={yedekParcalar}
        loading={loadingModalSearch} // Modal içindeki loading durumu
        searchTerm={yedekParcaSearchTerm}
      />

      {/* Yedek Parça Bilgileri Modal */}
      {currentSparePartInfo && (
        <SparePartInfoModal
          isOpen={sparePartInfoModalOpen}
          onClose={() => {
            setSparePartInfoModalOpen(false);
            setCurrentSparePartInfo(null);
          }}
          sparePart={currentSparePartInfo.part}
          quantity={currentSparePartInfo.quantity}
          onQuantityChange={handleSparePartQuantityChange}
          onSave={handleSparePartSave}
        />
      )}
    </div>
  );
};

export default PartSelectionStep;
